"use client";
import VideoCard from "./VideoCard";
import PlayButton from "./PlayButton";
import Image from "next/image";

export default function HeroSection() {
  return (
    <div className="min-h-screen bg-white">
      <main className="w-full px-6 md:px-12 lg:px-24 xl:px-36 py-12 md:py-20 lg:py-28">
        <div className="max-w-[1636px] mx-auto space-y-24 lg:space-y-28">
          
          {/* Hero Section */}
          <section className="flex flex-col sm:grid sm:grid-cols-[1.2fr_0.4fr] gap-5 sm:gap-8 lg:gap-8 sm:items-end">
            <div className="flex flex-col gap-6 md:gap-8">
              <div className="relative w-full h-auto rounded-2xl overflow-hidden aspect-[2.2/1] order-1">
                <Image 
                  src="/images/first-image.webp" 
                  alt="Team collaboration" 
                  fill
                  className="object-cover"
                  priority
                />
              </div>
              <h1 className="text-3xl sm:text-2xl md:text-3xl lg:text-[42px] xl:text-[50px] 2xl:text-[56px] font-normal leading-tight lg:leading-[56px] order-3 sm:order-none hidden sm:block">
                At <span className="text-[#3A599C]">Hyrelancer</span>, we&apos;re reimagining the way people work and connect.
              </h1>
            </div>
            <div className="grid grid-cols-2 sm:block gap-5 order-2">
              <div className="h-auto rounded-2xl overflow-hidden flex items-center justify-center bg-black">
                <a
                  href="https://www.instagram.com/reel/DLzd4IEBjtx/?igsh=ZnMwdHozcDc5bjhk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full"
                  tabIndex={-1}
                  aria-label="Watch on Instagram"
                >
                  <video
                    src="/images/hyrelanerinsta1.mp4"
                    className="object-cover w-full h-full pointer-events-none"
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster="/images/Subtract.png"
                    style={{ display: 'block', width: '100%', height: '100%' }}
                  >
                    Sorry, your browser doesn't support embedded videos.
                  </video>
                  <span className="sr-only">Watch video on Instagram</span>
                </a>
              </div>
              <div className="flex items-center sm:hidden">
                <h1 className="text-3xl font-normal leading-tight break-words">
                  At <span className="text-[#3A599C] whitespace-normal">Hyrelancer</span> we&apos;re reimagining the way people work and connect.
                </h1>
              </div>
            </div>
          </section>

          {/* Mission Section */}
          <section className="grid grid-cols-1 md:grid-cols-[0.4fr_1.2fr] gap-8 lg:gap-16 xl:gap-24 2xl:gap-44 items-center">
            <div className="h-auto order-2 md:order-1 w-full sm:w-1/2 md:w-full mx-auto rounded-2xl overflow-hidden flex items-center justify-center bg-black">
              <a
                href="https://www.instagram.com/reel/DNsfMvDwk5q/?igsh=MTE2bnNnN3RxN2FzZQ=="
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Watch on Instagram"
                className="block w-full h-full"
                tabIndex={-1}
              >
                <video
                  src="/images/hyrelancerinsta2.mp4"
                  className="object-cover w-full h-full pointer-events-none"
                  autoPlay
                  loop
                  muted
                  playsInline
                  poster="/images/subtract2.png"
                  style={{ display: 'block', width: '100%', height: '100%' }}
                >
                  Sorry, your browser doesn't support embedded videos.
                </video>
                <span className="sr-only">Watch video on Instagram</span>
              </a>
            </div>
            <div className="flex flex-col gap-3 md:gap-2 lg:gap-5 xl:gap-7 2xl:gap-9 order-1 md:order-2">
              <p className="text-base md:text-base lg:text-lg xl:text-xl leading-relaxed lg:leading-[24px] xl:leading-[28px] text-black">
              At Hyrelancer, we believe talent should never go unnoticed and opportunity should never be out of reach.
              Across India, countless people possess valuable skills  from students and creators to professionals and small business owners  all deserving a fair chance to earn, grow, and shine.
              </p>
              <p className="text-base md:text-base lg:text-lg xl:text-xl leading-relaxed lg:leading-[24px] xl:leading-[28px] text-black">
              Our mission is simple yet powerful: to increase the earning potential of individuals and strengthen India&apos;s collective economic power.
              By connecting passionate freelancers with businesses and individuals who need their skills, we make it easier for people to turn their talent into sustainable income  whether full-time or as a meaningful side hustle.
              </p>
              <p className="text-base md:text-base lg:text-lg xl:text-xl leading-relaxed lg:leading-[24px] xl:leading-[28px] text-black">
              Hyrelancer is more than a freelancing platform  it&apos;s a movement towards financial independence and recognition for skilled Indians.
              Here, every project is a partnership, every connection a chance to grow, and every success story a step toward a stronger, self-reliant India.
              </p>
              <p className="text-base md:text-base lg:text-lg xl:text-xl leading-relaxed lg:leading-[24px] xl:leading-[28px] text-black">
              We&apos;re not just reimagining freelancing  we&apos;re building a community where skill meets opportunity, and effort meets reward.
              </p>
            </div>
          </section>

          {/* CTA Section */}
          <section className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 items-center">
            <div className="w-full lg:w-[522px] h-[400px] md:h-[500px] lg:h-[557px] rounded-[32px] shadow-[0_4px_20px_0_rgba(58,89,156,0.18)] bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
            <a
                href="/login"
                aria-label="Go to login page"
                className="absolute inset-0 w-full h-full block cursor-pointer z-10"
                tabIndex={0}
                style={{ outline: 'none' }}
              >
                <video
                  className="w-full h-full object-cover pointer-events-none"
                  src="/images/animations.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  tabIndex={-1}
                  style={{ display: 'block', width: '100%', height: '100%' }}
                >
                  Sorry, your browser doesn't support embedded videos.
                </video>
                <span className="sr-only">Go to login page</span>
              </a>
            </div>
            
            <div className="relative">
              <div className="relative rounded-4xl  min-h-[400px] md:min-h-[500px] lg:min-h-[551px] flex items-start overflow-hidden py-6">
                {/* Background Video */}
                <video
                  className="absolute inset-0 w-full h-full object-cover z-0"
                  src="/images/videohyre.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{ display: 'block', width: '100%', height: '100%' }}
                >
                  Sorry, your browser doesn't support embedded videos.
                </video>

                <div className="relative z-20 w-full px-4 md:px-12 lg:px-12">
                  <h2 className="text-2xl  xs:text-[12px]sm:text-[14px] md:text-4xl lg:text-[28px] xl:text-[35px] 2xl:text-[56px] font-normal leading-tight lg:leading-[34px] xl:leading-[56px] text-white max-w-[988px]">
                  Be part of a movement that uplifts Indian freelancers  where every skill matters- 
                  every dream is valid, and every opportunity leads to growth.
                  </h2>
                </div>
              </div>
              
              <PlayButton 
                variant="arrow"
                className="absolute -bottom-0 "
              />
            </div>
          </section>

          {/* Our Story Section */}
          <section className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 sm:gap-8 md:gap-12 lg:gap-16 xl:gap-20 2xl:gap-24 py-12 md:py-16 lg:py-20">
            <div className="flex flex-col gap-4 sm:gap-5 md:gap-6">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[56px] 2xl:text-[64px] font-bold leading-tight lg:leading-[56px] xl:leading-[64px] text-black">
                Our Story
              </h2>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-[28px] leading-relaxed md:leading-[28px] lg:leading-[32px] xl:leading-[36px] text-black font-normal">
                Every great idea starts with a need – and <br />ours was simple: to make freelancing effortless, <br/> fair, and full of opportunity.
              </p>
            </div>
        
            <div className="flex flex-col gap-4 sm:gap-5 md:gap-6 lg:gap-8">
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-[28px] leading-relaxed md:leading-[28px] lg:leading-[32px] xl:leading-[36px] text-black font-normal">
                We created Hyrelancer to connect passionate freelancers with businesses that value quality, creativity, and trust. No clutter, no confusion – just a clean, smart way to work together.
              </p>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-[28px] leading-relaxed md:leading-[28px] lg:leading-[32px] xl:leading-[36px] text-black font-normal">
                At Hyrelancer, we celebrate independence, collaboration, and growth. Because when the right people connect, amazing things happen.
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

